import React, { useEffect, useState } from "react";
import { Copy, Share2, Gift, Award, Banknote, Clock, MessageCircle, Mail, Send, CheckCircle2, MousePointerClick, ShoppingBag, Circle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AccountLayout from "../../components/AccountLayout";
import { api } from "../../lib/api";
import { buildOgShareUrl, shareLinks } from "../../lib/affiliate";

const STATUS_STYLES = {
  pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  approved: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  paid_out: "bg-[#CFF048]/15 text-[#CFF048] border-[#CFF048]/40",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

function Statbox({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-5">
      <div className="flex items-center gap-2 text-[#71717A]">
        <Icon size={14} className="text-[#CFF048]" />
        <p className="label-mono">{label}</p>
      </div>
      <p className="font-display font-black text-3xl mt-2">{value}</p>
      {sub && <p className="label-mono text-[#71717A] mt-1 text-[10px]">{sub}</p>}
    </div>
  );
}

export default function Affiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState("");
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/affiliate/me").then((r) => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  const loadInvites = () => {
    setInvitesLoading(true);
    api.get("/affiliate/share-history?limit=100")
      .then((r) => setInvites(r.data || []))
      .catch(() => {})
      .finally(() => setInvitesLoading(false));
  };
  useEffect(() => { load(); loadInvites(); }, []);
  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data || []);
      if (r.data?.length) setActiveProduct(r.data[0].slug);
    });
  }, []);

  if (loading || !data) {
    return <AccountLayout><div className="p-10 text-center label-mono text-[#71717A]">// LOADING DASHBOARD...</div></AccountLayout>;
  }

  const code = data.code;
  const perUnit = data.commission_per_unit;
  const baseShareUrl = activeProduct ? buildOgShareUrl(activeProduct, code) : `${window.location.origin}?ref=${code}`;
  const shareText = activeProduct
    ? `Check this Cult Lyf tee — use my code ${code} so we both win.`
    : `Shop Cult Lyf streetwear — use my code ${code}.`;
  const links = shareLinks({ url: baseShareUrl, text: shareText, title: "Cult Lyf" });

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("COPIED"),
      () => toast.error("Couldn't copy")
    );
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ url: baseShareUrl, text: shareText, title: "Cult Lyf" }); }
      catch (e) { if (e?.name !== "AbortError") toast.error("Share failed"); }
    } else {
      copy(baseShareUrl);
    }
  };

  return (
    <AccountLayout>
      <div className="space-y-6" data-testid="affiliate-page">
        {/* HERO */}
        <div className="border border-[#CFF048] bg-gradient-to-br from-[#0A0A0A] to-black p-6 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 opacity-10 text-[#CFF048]">
            <Gift size={160} />
          </div>
          <p className="label-mono text-[#CFF048]">// SELL & EARN</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mt-2">
            Earn ₹{perUnit} per tee.
          </h2>
          <p className="text-[#A1A1AA] max-w-xl mt-2">
            Your code below auto-attaches to any product link you share. When someone buys, you earn —
            no minimum payout, paid via UPI / bank transfer.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="border-2 border-[#CFF048] bg-black px-5 py-3" data-testid="affiliate-code">
              <p className="label-mono text-[#71717A]">// YOUR CODE</p>
              <p className="font-display font-black text-2xl text-[#CFF048] tracking-wider">{code}</p>
            </div>
            <button
              onClick={() => copy(code)}
              className="border border-[#27272A] hover:border-[#CFF048] hover:text-[#CFF048] px-4 py-3 label-mono flex items-center gap-2"
              data-testid="copy-code-btn"
            >
              <Copy size={14} /> COPY CODE
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="affiliate-stats">
          <Statbox icon={Clock} label="PENDING" value={`₹${data.totals.pending}`} sub="Order placed, awaiting confirmation" />
          <Statbox icon={Award} label="APPROVED" value={`₹${data.totals.approved}`} sub="Confirmed · about to be paid out" />
          <Statbox icon={Banknote} label="PAID OUT" value={`₹${data.totals.paid_out}`} sub="Transferred to your account" />
          <Statbox icon={Gift} label="LIFETIME" value={`₹${data.totals.lifetime}`} sub={`${data.totals.orders} referred order${data.totals.orders === 1 ? '' : 's'}`} />
        </div>

        {/* SHARE PANEL */}
        <div className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="share-panel">
          <p className="label-mono text-[#CFF048]">// SHARE THE LINK</p>
          <h3 className="font-display font-black text-xl mt-2">Pick a tee, drop the link.</h3>
          <p className="text-[#A1A1AA] text-sm mt-2 max-w-xl">
            The link auto-includes your code. When the buyer pays, you earn ₹{perUnit} per tee in their order.
            Want to share the homepage instead? Leave product unselected.
          </p>

          <div className="grid sm:grid-cols-[1fr_auto] gap-3 mt-4">
            <select
              value={activeProduct}
              onChange={(e) => setActiveProduct(e.target.value)}
              className="cult-input"
              data-testid="share-product-select"
            >
              <option value="">— Share homepage —</option>
              {products.map((p) => (
                <option key={p.id} value={p.slug}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={nativeShare}
              className="btn-neon flex items-center gap-2"
              data-testid="affiliate-share-btn"
            >
              <Share2 size={14} /> NATIVE SHARE
            </button>
          </div>

          {/* URL + copy */}
          <div className="mt-4 flex items-center gap-2 border border-[#27272A] p-3 bg-black">
            <p className="label-mono text-[#71717A] text-[10px] truncate flex-1" data-testid="share-url">{baseShareUrl}</p>
            <button
              onClick={() => copy(baseShareUrl)}
              className="text-[#CFF048] hover:text-white"
              aria-label="Copy URL"
              data-testid="copy-url-btn"
            >
              <Copy size={16} />
            </button>
          </div>

          {/* Platform buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
            <a href={links.whatsapp} target="_blank" rel="noreferrer noopener" className="px-3 py-2 label-mono border border-[#27272A] hover:border-[#25D366] hover:text-[#25D366] flex items-center justify-center gap-2" data-testid="share-whatsapp">
              <MessageCircle size={14} /> WHATSAPP
            </a>
            <a href={links.facebook} target="_blank" rel="noreferrer noopener" className="px-3 py-2 label-mono border border-[#27272A] hover:border-[#1877F2] hover:text-[#1877F2] flex items-center justify-center gap-2" data-testid="share-facebook">
              FACEBOOK
            </a>
            <a href={links.twitter} target="_blank" rel="noreferrer noopener" className="px-3 py-2 label-mono border border-[#27272A] hover:border-white flex items-center justify-center gap-2" data-testid="share-twitter">
              X / TWITTER
            </a>
            <a href={links.email} className="px-3 py-2 label-mono border border-[#27272A] hover:border-[#CFF048] hover:text-[#CFF048] flex items-center justify-center gap-2" data-testid="share-email">
              <Mail size={14} /> EMAIL
            </a>
            <a href={links.telegram} target="_blank" rel="noreferrer noopener" className="px-3 py-2 label-mono border border-[#27272A] hover:border-[#2AABEE] hover:text-[#2AABEE] flex items-center justify-center gap-2" data-testid="share-telegram">
              TELEGRAM
            </a>
          </div>

          {/* ZEPTOMAIL EMAIL SHARE — high-conversion personalised invite */}
          <EmailShareForm
            products={products}
            defaultSlug={activeProduct}
            perUnit={perUnit}
            code={code}
            onSent={loadInvites}
          />
        </div>

        {/* EMAIL INVITES HISTORY TABLE */}
        <EmailInvitesTable invites={invites} loading={invitesLoading} products={products} onRefresh={loadInvites} />

        {/* EARNINGS TABLE */}
        <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="earnings-table">
          <div className="p-5 border-b border-[#27272A] flex items-center justify-between">
            <p className="font-display font-black text-lg">Who Bought What</p>
            <p className="label-mono text-[#71717A]">// {data.earnings.length} ORDER{data.earnings.length === 1 ? '' : 'S'}</p>
          </div>
          {data.earnings.length === 0 ? (
            <div className="p-10 text-center" data-testid="no-earnings">
              <p className="label-mono text-[#71717A]">// NO REFERRED ORDERS YET</p>
              <p className="text-[#A1A1AA] text-sm mt-2">Share your code and your first ₹{perUnit} will land here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black border-b border-[#27272A]">
                  <tr className="label-mono text-[#71717A]">
                    <th className="text-left p-3">DATE</th>
                    <th className="text-left p-3">BUYER</th>
                    <th className="text-left p-3">ITEMS</th>
                    <th className="text-left p-3">UNITS</th>
                    <th className="text-left p-3">EARNED</th>
                    <th className="text-left p-3">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.earnings.map((e) => (
                    <tr key={e.id} className="border-b border-[#18181B] hover:bg-[#18181B]" data-testid={`earning-${e.id}`}>
                      <td className="p-3 label-mono text-[#71717A] whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <p className="truncate">{e.buyer_name || "—"}</p>
                        <p className="label-mono text-[#71717A] truncate text-[10px]">{e.buyer_email}</p>
                      </td>
                      <td className="p-3">
                        <ul className="space-y-0.5">
                          {(e.items || []).map((it, i) => (
                            <li key={i} className="truncate"><span className="text-[#A1A1AA]">{it.name}</span> <span className="label-mono text-[#71717A]">× {it.qty}</span></li>
                          ))}
                        </ul>
                      </td>
                      <td className="p-3">{e.units}</td>
                      <td className="p-3 text-[#CFF048] font-bold">₹{e.amount}</td>
                      <td className="p-3">
                        <span className={`label-mono ${STATUS_STYLES[e.status] || STATUS_STYLES.pending} border px-2 py-0.5 text-[10px] uppercase`}>{e.status.replace("_", " ")}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}

function EmailShareForm({ products, defaultSlug, perUnit, code, onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState("");
  const [productSlug, setProductSlug] = useState(defaultSlug || "");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { email, remaining_today }

  useEffect(() => { setProductSlug(defaultSlug || ""); }, [defaultSlug]);

  const selectedProduct = products.find((p) => p.slug === productSlug);

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim())) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/affiliate/share-email", {
        to_email: toEmail.trim(),
        to_name: toName.trim(),
        product_slug: productSlug || null,
        personal_note: note.trim(),
      });
      setSent({ email: data.sent_to, remaining: data.remaining_today });
      setToEmail("");
      setToName("");
      setNote("");
      toast.success("EMAIL SENT", { description: `Delivered to ${data.sent_to}. Your code ${code} is auto-attached.` });
      if (typeof onSent === "function") onSent();
    } catch (err) {
      toast.error("COULDN'T SEND", { description: err?.response?.data?.detail || "Try again in a bit." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-2 border-[#CFF048] bg-black mt-6 p-5 md:p-6" data-testid="email-share-form">
      <div className="flex items-center gap-2 flex-wrap">
        <Mail size={16} className="text-[#CFF048]" />
        <p className="label-mono text-[#CFF048]">// SEND VIA EMAIL</p>
      </div>
      <h4 className="font-display font-black text-xl md:text-2xl mt-2 leading-none uppercase">
        Drop this in your friend&apos;s inbox.
      </h4>
      <p className="text-[#A1A1AA] text-sm mt-2">
        Way more personal than a WhatsApp forward. We&apos;ll send a branded email with the t-shirt design,
        a <strong className="text-[#CFF048]">BUY NOW</strong> button and your code <strong className="text-[#CFF048]">{code}</strong> already baked in — you earn ₹{perUnit}/tee when they buy.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <div>
          <label className="label-mono text-[#71717A] text-[10px]">// FRIEND&apos;S EMAIL *</label>
          <input
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="friend@example.com"
            className="cult-input w-full mt-1"
            data-testid="share-email-to"
          />
        </div>
        <div>
          <label className="label-mono text-[#71717A] text-[10px]">// FRIEND&apos;S NAME (OPTIONAL)</label>
          <input
            type="text"
            value={toName}
            onChange={(e) => setToName(e.target.value)}
            placeholder="Ravi"
            className="cult-input w-full mt-1"
            data-testid="share-email-name"
          />
        </div>
      </div>

      <div className="mt-3">
        <label className="label-mono text-[#71717A] text-[10px]">// TEE TO SHARE (OR HOMEPAGE)</label>
        <select
          value={productSlug}
          onChange={(e) => setProductSlug(e.target.value)}
          className="cult-input w-full mt-1"
          data-testid="share-email-product"
        >
          <option value="">— Share homepage —</option>
          {products.map((p) => (
            <option key={p.id} value={p.slug}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="mt-3">
        <label className="label-mono text-[#71717A] text-[10px]">// PERSONAL NOTE (OPTIONAL · 400 CHARS)</label>
        <textarea
          value={note}
          maxLength={400}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="This one's got your name on it. Cult Lyf drop — heavyweight cotton, one of the last few."
          className="cult-input w-full mt-1 resize-none"
          data-testid="share-email-note"
        />
        <p className="label-mono text-[#71717A] text-[9px] text-right mt-1">{note.length}/400</p>
      </div>

      {selectedProduct && (
        <div className="mt-4 border border-[#27272A] p-3 flex items-center gap-3 bg-[#0A0A0A]">
          <img
            src={selectedProduct.images?.[0]}
            alt=""
            className="w-14 h-14 object-cover border border-[#27272A]"
          />
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold truncate">{selectedProduct.name}</p>
            <p className="label-mono text-[#71717A] mt-0.5 text-[10px]">
              // PREVIEW · The email hero will show this tee&apos;s design + a big BUY NOW button.
            </p>
          </div>
          <p className="font-mono-cult text-[#CFF048] font-bold hidden sm:block">₹{selectedProduct.price?.toFixed?.(0) ?? selectedProduct.price}</p>
        </div>
      )}

      <button
        onClick={submit}
        disabled={sending || !toEmail}
        className="btn-neon w-full mt-5 disabled:opacity-60"
        data-testid="share-email-submit"
      >
        <Send size={14} className="mr-2" />
        {sending ? "SENDING…" : "SEND"}
      </button>

      {sent && (
        <div className="mt-4 border border-[#CFF048] p-3 flex items-start gap-3" data-testid="share-email-success">
          <CheckCircle2 size={18} className="text-[#CFF048] mt-0.5" />
          <div>
            <p className="label-mono text-[#CFF048]">// DELIVERED</p>
            <p className="text-sm text-white mt-1">
              Sent to <strong>{sent.email}</strong>. When they buy, ₹{perUnit}/tee lands in your account.
              {" "}
              <span className="text-[#71717A]">// {sent.remaining} share{sent.remaining === 1 ? "" : "s"} left today.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function InviteStatusPill({ invite }) {
  if (invite.bought) {
    return (
      <span className="inline-flex items-center gap-1 label-mono text-[10px] px-2 py-1 border border-[#CFF048] bg-[#CFF048]/10 text-[#CFF048]" data-testid={`invite-status-bought-${invite.id}`}>
        <ShoppingBag size={10} /> BOUGHT
      </span>
    );
  }
  if ((invite.clicks || 0) > 0) {
    return (
      <span className="inline-flex items-center gap-1 label-mono text-[10px] px-2 py-1 border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 text-[#B896FF]" data-testid={`invite-status-clicked-${invite.id}`}>
        <MousePointerClick size={10} /> CLICKED · {invite.clicks}×
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 label-mono text-[10px] px-2 py-1 border border-[#27272A] text-[#71717A]" data-testid={`invite-status-sent-${invite.id}`}>
      <Circle size={9} className="text-[#71717A]" /> SENT
    </span>
  );
}

function EmailInvitesTable({ invites, loading, products, onRefresh }) {
  const productBySlug = React.useMemo(() => {
    const m = {};
    for (const p of products) m[p.slug] = p;
    return m;
  }, [products]);

  const sent = invites.length;
  const clicked = invites.filter((i) => (i.clicks || 0) > 0).length;
  const bought = invites.filter((i) => i.bought).length;
  const clickRate = sent > 0 ? Math.round((clicked / sent) * 100) : 0;
  const conversionRate = clicked > 0 ? Math.round((bought / clicked) * 100) : 0;

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="email-invites-table">
      <div className="p-5 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-mono text-[#CFF048] flex items-center gap-2">
            <Mail size={14} /> // MY EMAIL INVITES
          </p>
          <p className="font-display font-black text-lg mt-1">The funnel behind your commissions.</p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-outline flex items-center gap-2 text-xs"
          data-testid="invites-refresh-btn"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> REFRESH
        </button>
      </div>

      {/* FUNNEL STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-[#27272A]" data-testid="invites-funnel">
        <div className="p-4 border-r border-[#27272A]">
          <p className="label-mono text-[#71717A] text-[10px]">// SENT</p>
          <p className="font-display font-black text-2xl text-white mt-1">{sent}</p>
        </div>
        <div className="p-4 border-r border-[#27272A]">
          <p className="label-mono text-[#71717A] text-[10px]">// CLICKED</p>
          <p className="font-display font-black text-2xl text-[#B896FF] mt-1">{clicked}</p>
          <p className="label-mono text-[#71717A] text-[9px] mt-0.5">{clickRate}% CTR</p>
        </div>
        <div className="p-4 border-r border-[#27272A]">
          <p className="label-mono text-[#71717A] text-[10px]">// BOUGHT</p>
          <p className="font-display font-black text-2xl text-[#CFF048] mt-1">{bought}</p>
          <p className="label-mono text-[#71717A] text-[9px] mt-0.5">{conversionRate}% CONV</p>
        </div>
        <div className="p-4">
          <p className="label-mono text-[#71717A] text-[10px]">// FUNNEL</p>
          <p className="font-mono-cult text-sm text-white mt-1">{sent} → {clicked} → {bought}</p>
          <p className="label-mono text-[#71717A] text-[9px] mt-0.5">SENT → CLICKED → BOUGHT</p>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center label-mono text-[#71717A]">// LOADING INVITES...</div>
      ) : invites.length === 0 ? (
        <div className="p-10 text-center" data-testid="no-invites">
          <p className="label-mono text-[#71717A]">// NO INVITES YET</p>
          <p className="text-[#A1A1AA] text-sm mt-2">Send your first invite above — you&apos;ll see clicks and conversions land here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black border-b border-[#27272A]">
              <tr className="label-mono text-[#71717A]">
                <th className="text-left p-3">SENT</th>
                <th className="text-left p-3">TO</th>
                <th className="text-left p-3">TEE</th>
                <th className="text-left p-3">STATUS</th>
                <th className="text-left p-3">CLICKED AT</th>
                <th className="text-left p-3">BOUGHT AT</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => {
                const p = inv.product_slug ? productBySlug[inv.product_slug] : null;
                return (
                  <tr key={inv.id} className="border-b border-[#18181B] hover:bg-[#111]" data-testid={`invite-row-${inv.id}`}>
                    <td className="p-3 label-mono text-[#71717A] text-[11px] whitespace-nowrap">{fmtDate(inv.sent_at)}</td>
                    <td className="p-3">
                      <p className="text-white truncate max-w-[220px]">{inv.to_email}</p>
                      {inv.to_name && <p className="label-mono text-[#71717A] text-[10px]">{inv.to_name}</p>}
                    </td>
                    <td className="p-3">
                      {p ? (
                        <div className="flex items-center gap-2">
                          <img src={p.images?.[0]} alt="" className="w-9 h-9 object-cover border border-[#27272A]" />
                          <span className="text-white text-[13px] truncate max-w-[160px]">{p.name}</span>
                        </div>
                      ) : (
                        <span className="label-mono text-[#71717A]">HOMEPAGE</span>
                      )}
                    </td>
                    <td className="p-3"><InviteStatusPill invite={inv} /></td>
                    <td className="p-3 label-mono text-[#71717A] text-[11px] whitespace-nowrap">{fmtDate(inv.clicked_at)}</td>
                    <td className="p-3 label-mono text-[#71717A] text-[11px] whitespace-nowrap">{inv.bought ? fmtDate(inv.bought_at) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

